/**
 * StatsRenderer
 *
 * Renders network stats in a dynamic grid layout.
 * Layout: 3 rows max, unlimited columns (expands right).
 * Supports blank spaces and drag-to-reorder.
 */

import { Container, Text, Graphics, FederatedPointerEvent, BlurFilter } from 'pixi.js';
import type { BlockchainState } from '../stores/blockchainState';
import { userSettings, STAT_LABELS, type StatKey, getStatLabel, parseAddressStatKey } from '../stores/userSettings';
import { SCENE_WIDTH } from '../config/visualization.config';
import { ThemeManager } from './ThemeManager';
import type { PixiThemeColors } from '../config/themes';

interface StatBlock {
  container: Container;
  bg: Graphics;
  sparkline: Graphics;
  label: Text;
  value: Text;
  pinIcon: Graphics;
  resizeHandle: Graphics;
  key: string;
  gridIndex: number;
  widthMultiplier: number;
  // Sparkline animation state
  sparklineLastLength: number;
  sparklineLastMin: number;
  sparklineLastMax: number;
  sparklineTargetMin: number;
  sparklineTargetMax: number;
}

// Import CircleMetrics from CircleRenderer
import type { CircleMetrics } from './CircleRenderer';

export class StatsRenderer {
  private container: Container;
  private stats: Map<string, StatBlock> = new Map();
  private moreStatsButton!: Container;
  private moreStatsButtonBg!: Graphics;
  private moreStatsButtonText!: Text;
  private state: BlockchainState;
  private onMoreStatsClick: ((mouseX: number, mouseY: number) => void) | null = null;

  // Callback to get circle metrics by address
  private getCircleMetrics: ((address: string) => CircleMetrics | null) | null = null;
  // Callback to get project name by address
  private getProjectName: ((address: string) => string | null) | null = null;

  // Track previous values for flash effect
  private prevValues: Map<string, string> = new Map();
  private flashTimers: Map<string, number> = new Map();

  // Cleanup functions
  private unsubscribeSettings: (() => void) | null = null;

  // Drag state
  private dragging: StatBlock | null = null;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragOffsetX = 0;
  private dragOffsetY = 0;
  private dragOriginalIndex = -1;

  // Resize state
  private resizing: StatBlock | null = null;
  private resizeStartX = 0;
  private resizeStartWidth = 1;
  private resizeCurrentX = 0;

  private readonly STATS_X = 15;
  private readonly COL_WIDTH = 115;
  private readonly ROW_HEIGHT = 100;
  private readonly BLOCK_WIDTH = 105;
  private readonly BLOCK_HEIGHT = 90;
  private readonly MAX_ROWS = 3; // For new stat spawning only
  private readonly GRID_ROWS = 100; // For index calculations - allows stats anywhere on page

  // Theme colors (reactive)
  private colors: PixiThemeColors;
  private unsubscribeTheme: (() => void) | null = null;

  // Default stats to show on first load (fallback if userSettings has empty grid)
  private readonly DEFAULT_STATS: string[] = ['mbps', 'fps', 'gps', 'bps', 'tps', 'block'];

  constructor(parentContainer: Container, state: BlockchainState) {
    this.container = new Container();
    this.state = state;
    this.container.x = this.STATS_X;
    this.container.y = 5;
    parentContainer.addChild(this.container);

    // Initialize theme colors
    this.colors = ThemeManager.colors;

    // Subscribe to theme changes
    this.unsubscribeTheme = ThemeManager.subscribe((colors) => {
      this.colors = colors;
      this.rebuildStats();
    });

    // Initialize defaults if grid is empty
    this.initializeDefaults();

    // "More Stats" button
    this.createMoreStatsButton();

    // Create stat blocks
    this.rebuildStats();

    // Subscribe to settings changes
    this.unsubscribeSettings = userSettings.subscribe(() => this.rebuildStats());

    // console.log('📊 StatsRenderer initialized - grid with blank spaces');
  }

  private initializeDefaults(): void {
    if (Object.keys(userSettings.statGrid).length === 0) {
      // Place default stats in a grid: columns first, rows second
      // Grid index = col * GRID_ROWS + row (where GRID_ROWS = 100)
      // Max 3 rows, expand columns as needed
      const MAX_ROWS = 3;

      this.DEFAULT_STATS.forEach((key, i) => {
        const col = Math.floor(i / MAX_ROWS);
        const row = i % MAX_ROWS;
        const gridIndex = this.positionToIndex(col, row);
        userSettings.setStatAt(gridIndex, key);
      });
    }
  }

  private indexToPosition(index: number): { col: number; row: number } {
    return {
      col: Math.floor(index / this.GRID_ROWS),
      row: index % this.GRID_ROWS,
    };
  }

  private positionToIndex(col: number, row: number): number {
    return col * this.GRID_ROWS + row;
  }

  /**
   * Build a map of ALL visually occupied positions -> stat key
   * This accounts for wide stats occupying multiple columns
   */
  private buildOccupancyMap(excludeKey?: string): Map<number, string> {
    const occupancy = new Map<number, string>();
    const grid = userSettings.statGrid;

    for (const [indexStr, key] of Object.entries(grid)) {
      if (excludeKey && key === excludeKey) continue;

      const idx = Number(indexStr);
      const width = userSettings.getStatWidth(key);
      const { col, row } = this.indexToPosition(idx);

      // Mark all positions this stat visually occupies
      for (let w = 0; w < width; w++) {
        const occupiedIndex = this.positionToIndex(col + w, row);
        occupancy.set(occupiedIndex, key);
      }
    }

    return occupancy;
  }

  /**
   * Find a stat's anchor index (where it's stored in the grid)
   */
  private findStatAnchorIndex(key: string): number {
    const grid = userSettings.statGrid;
    for (const [indexStr, k] of Object.entries(grid)) {
      if (k === key) return Number(indexStr);
    }
    return -1;
  }

  /**
   * Find the first grid slot that can fit a stat of given width
   * @param width - Width of the stat in columns
   * @param blocked - Set of blocked positions
   * @param constrainToRows - If true, only search within MAX_ROWS (for new stats/displaced stats)
   */
  private findFirstFittingSlot(width: number, blocked: Set<number>, constrainToRows: boolean = true): number {
    // Search column by column, row by row
    const maxCols = 20; // Reasonable limit
    const maxRows = constrainToRows ? this.MAX_ROWS : this.GRID_ROWS;

    for (let col = 0; col < maxCols; col++) {
      for (let row = 0; row < maxRows; row++) {
        let canFit = true;

        for (let w = 0; w < width; w++) {
          if (blocked.has(this.positionToIndex(col + w, row))) {
            canFit = false;
            break;
          }
        }

        if (canFit) return this.positionToIndex(col, row);
      }
    }
    return -1;
  }

  private rebuildStats(): void {
    // Don't rebuild while dragging or resizing
    if (this.dragging || this.resizing) return;

    // Clear existing stat blocks
    for (const [, stat] of this.stats) {
      stat.container.destroy({ children: true });
    }
    this.stats.clear();
    this.prevValues.clear();
    this.flashTimers.clear();

    // Get stat grid
    const grid = userSettings.statGrid;

    // Build a set of positions blocked by wide stats
    const blockedPositions = new Set<number>();
    for (const [indexStr, key] of Object.entries(grid)) {
      const idx = Number(indexStr);
      const width = userSettings.getStatWidth(key);
      if (width > 1) {
        const { col, row } = this.indexToPosition(idx);
        // Block positions in the extra columns (not the first column where stat is placed)
        for (let w = 1; w < width; w++) {
          blockedPositions.add(this.positionToIndex(col + w, row));
        }
      }
    }

    // Create stat blocks at their grid positions (skip blocked positions)
    for (const [indexStr, key] of Object.entries(grid)) {
      const index = Number(indexStr);
      // Skip if this position is blocked by a wide stat
      if (blockedPositions.has(index)) continue;

      const { col, row } = this.indexToPosition(index);
      // Get label - for address stats, try to get project name
      const parsed = parseAddressStatKey(key);
      const projectName = parsed && this.getProjectName ? this.getProjectName(parsed.address) : undefined;
      const label = getStatLabel(key, projectName || undefined);
      this.createStatBlock(key, label, col, row, index);
    }

    // Update button position
    this.updateMoreStatsButtonPosition();
  }

  private createStatBlock(key: string, label: string, col: number, row: number, gridIndex: number): void {
    const x = col * this.COL_WIDTH;
    const y = row * this.ROW_HEIGHT;
    const widthMultiplier = userSettings.getStatWidth(key);
    // Width spans grid columns (minus gap for spacing)
    const blockWidth = widthMultiplier * this.COL_WIDTH - 10;

    const blockContainer = new Container();
    blockContainer.x = x;
    blockContainer.y = y;
    blockContainer.eventMode = 'static';
    blockContainer.cursor = 'grab';

    // Background with backdrop blur effect
    const blurBg = new Graphics();
    blurBg.roundRect(0, 0, blockWidth, this.BLOCK_HEIGHT, 4);
    blurBg.fill({ color: this.colors.bgPrimary, alpha: 0.5 });
    blurBg.filters = [new BlurFilter({ strength: 8, quality: 3 })];
    blockContainer.addChild(blurBg);

    // Main background - uses theme surface color
    const bg = new Graphics();
    bg.roundRect(0, 0, blockWidth, this.BLOCK_HEIGHT, 4);
    bg.fill({ color: this.colors.bgSurface, alpha: 1 });
    bg.stroke({ width: 1, color: this.colors.border, alpha: 0.5 });
    blockContainer.addChild(bg);

    // Sparkline (behind text, will be drawn in update)
    const sparkline = new Graphics();
    blockContainer.addChild(sparkline);

    // Foreground container for text elements
    const fgContainer = new Container();
    blockContainer.addChild(fgContainer);

    // Label - centered
    const labelText = new Text({
      text: label,
      style: {
        fontFamily: 'Inter, Arial, sans-serif',
        fontSize: 15,
        fontWeight: '700',
        fill: this.colors.accentPrimary,
        letterSpacing: 1.5,
      }
    });
    labelText.alpha = 0.9;
    labelText.anchor.set(0.5, 0);
    labelText.x = blockWidth / 2;
    labelText.y = 10;
    fgContainer.addChild(labelText);

    // Value - centered in box (white in dark mode, black in light mode)
    const value = new Text({
      text: '0',
      style: {
        fontFamily: 'Inter, Arial, sans-serif',
        fontSize: 28,
        fontWeight: '900',
        fill: this.colors.textPrimary,
        dropShadow: {
          color: this.colors.textPrimary,
          blur: 8,
          alpha: 0.2,
          distance: 0,
        },
      }
    });
    value.anchor.set(0.5, 0.5);
    value.x = blockWidth / 2;
    value.y = this.BLOCK_HEIGHT / 2 + 5; // Offset slightly for label
    fgContainer.addChild(value);

    // X icon to remove - hidden by default, shown on hover
    const pinIcon = new Graphics();
    pinIcon.x = blockWidth - 24;
    pinIcon.y = 6;
    pinIcon.eventMode = 'static';
    pinIcon.cursor = 'pointer';
    pinIcon.visible = false; // Hidden by default
    // Draw initial hit area (invisible rect for pointer detection)
    pinIcon.rect(0, 0, 18, 18);
    pinIcon.fill({ color: 0x000000, alpha: 0.01 });

    pinIcon.on('pointertap', (event: FederatedPointerEvent) => {
      event.stopPropagation();
      userSettings.unpinStat(key);
    });

    fgContainer.addChild(pinIcon);

    // Resize handle on right edge (positioned at right side of block)
    const resizeHandle = new Graphics();
    resizeHandle.rect(0, 0, 12, this.BLOCK_HEIGHT);
    resizeHandle.fill({ color: this.colors.accentPrimary, alpha: 0 }); // Invisible hit area
    resizeHandle.x = blockWidth - 6; // Position at right edge
    resizeHandle.eventMode = 'static';
    resizeHandle.cursor = 'ew-resize';
    fgContainer.addChild(resizeHandle);

    const statBlock: StatBlock = {
      container: blockContainer,
      bg,
      sparkline,
      label: labelText,
      value,
      pinIcon,
      resizeHandle,
      key,
      gridIndex,
      widthMultiplier,
      // Sparkline animation state
      sparklineLastLength: 0,
      sparklineLastMin: 0,
      sparklineLastMax: 0,
      sparklineTargetMin: 0,
      sparklineTargetMax: 0,
    };

    // Track if hovering over icon to prevent hiding during hover
    let isOverIcon = false;

    // Show/hide X icon on block hover
    blockContainer.on('pointerover', () => {
      pinIcon.visible = true;
      this.drawUnpinIconVisible(pinIcon);
    });
    blockContainer.on('pointerout', () => {
      // Don't hide if we're over the icon
      if (!isOverIcon) {
        pinIcon.visible = false;
      }
    });

    pinIcon.on('pointerover', () => {
      isOverIcon = true;
      this.drawUnpinIconHover(pinIcon);
    });
    pinIcon.on('pointerout', () => {
      isOverIcon = false;
      this.drawUnpinIconVisible(pinIcon);
    });

    // Resize handle hover effect
    resizeHandle.on('pointerover', () => {
      resizeHandle.clear();
      resizeHandle.rect(0, 0, 12, this.BLOCK_HEIGHT);
      resizeHandle.fill({ color: this.colors.accentPrimary, alpha: 0.3 });
    });
    resizeHandle.on('pointerout', () => {
      if (this.resizing !== statBlock) {
        resizeHandle.clear();
        resizeHandle.rect(0, 0, 12, this.BLOCK_HEIGHT);
        resizeHandle.fill({ color: this.colors.accentPrimary, alpha: 0 });
      }
    });

    // Resize handler
    resizeHandle.on('pointerdown', (event: FederatedPointerEvent) => {
      event.stopPropagation();
      this.startResize(statBlock, event);
    });

    // Drag handler (only if not on resize handle)
    blockContainer.on('pointerdown', (event: FederatedPointerEvent) => {
      if (event.target === pinIcon || event.target === resizeHandle) return;
      this.startDrag(statBlock, event);
    });

    this.container.addChild(blockContainer);
    this.stats.set(key, statBlock);
    this.prevValues.set(key, '');
    this.flashTimers.set(key, 0);
  }

  private startDrag(stat: StatBlock, event: FederatedPointerEvent): void {
    this.dragging = stat;
    // Use client coordinates (screen pixels) - matches onDragMove's event.clientX/Y
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragOffsetX = stat.container.x;
    this.dragOffsetY = stat.container.y;
    this.dragOriginalIndex = stat.gridIndex;

    // Bring to front
    this.container.removeChild(stat.container);
    this.container.addChild(stat.container);

    stat.container.alpha = 0.8;
    stat.container.cursor = 'grabbing';

    const onMove = (e: PointerEvent) => this.onDragMove(e);
    const onUp = () => {
      this.onDragEnd();
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }

  private onDragMove(event: PointerEvent): void {
    if (!this.dragging) return;

    // Account for viewport scale (screen pixels to world pixels)
    const scale = window.innerWidth / SCENE_WIDTH;
    const dx = (event.clientX - this.dragStartX) / scale;
    const dy = (event.clientY - this.dragStartY) / scale;

    this.dragging.container.x = this.dragOffsetX + dx;
    this.dragging.container.y = this.dragOffsetY + dy;
  }

  private onDragEnd(): void {
    if (!this.dragging) return;

    const draggedKey = this.dragging.key;
    const draggedWidth = userSettings.getStatWidth(draggedKey);
    const dragCenterX = this.dragging.container.x + this.BLOCK_WIDTH / 2;
    const dragCenterY = this.dragging.container.y + this.BLOCK_HEIGHT / 2;

    // Calculate target grid position - allow anywhere on page (no row constraint)
    let targetCol = Math.max(0, Math.floor(dragCenterX / this.COL_WIDTH));
    let targetRow = Math.max(0, Math.floor(dragCenterY / this.ROW_HEIGHT));
    const targetIndex = this.positionToIndex(targetCol, targetRow);

    // Reset visual state and snap to grid
    this.dragging.container.alpha = 1;
    this.dragging.container.cursor = 'grab';
    this.dragging.container.x = targetCol * this.COL_WIDTH;
    this.dragging.container.y = targetRow * this.ROW_HEIGHT;

    // If position hasn't changed, just clear the flag
    if (targetIndex === this.dragOriginalIndex) {
      this.dragging = null;
      return;
    }

    // Build occupancy map (excluding the dragged stat)
    const occupancy = this.buildOccupancyMap(draggedKey);

    // Find all stats that would be displaced by placing draggedKey at target position
    const statsToDisplace = new Map<string, number>(); // key -> anchor index

    for (let w = 0; w < draggedWidth; w++) {
      const checkIndex = this.positionToIndex(targetCol + w, targetRow);
      const occupyingKey = occupancy.get(checkIndex);
      if (occupyingKey && occupyingKey !== draggedKey && !statsToDisplace.has(occupyingKey)) {
        const anchorIndex = this.findStatAnchorIndex(occupyingKey);
        if (anchorIndex !== -1) {
          statsToDisplace.set(occupyingKey, anchorIndex);
        }
      }
    }

    // Build blocked set: positions occupied by stats NOT being displaced
    const blocked = new Set<number>();
    for (const [index, key] of occupancy) {
      if (!statsToDisplace.has(key)) {
        blocked.add(index);
      }
    }

    // Add positions the dragged stat will occupy
    for (let w = 0; w < draggedWidth; w++) {
      blocked.add(this.positionToIndex(targetCol + w, targetRow));
    }

    // Find new positions for displaced stats
    const moves: { fromIndex: number; toIndex: number }[] = [];

    // Get the original position for potential swap
    const originalCol = Math.floor(this.dragOriginalIndex / this.GRID_ROWS);
    const originalRow = this.dragOriginalIndex % this.GRID_ROWS;

    for (const [key, fromIndex] of statsToDisplace) {
      const width = userSettings.getStatWidth(key);

      // Try to place displaced stat at the dragged stat's original position (swap)
      let canSwapToOriginal = true;
      for (let w = 0; w < width; w++) {
        const checkIdx = this.positionToIndex(originalCol + w, originalRow);
        if (blocked.has(checkIdx)) {
          canSwapToOriginal = false;
          break;
        }
      }

      let newIndex: number;
      if (canSwapToOriginal) {
        // Swap: displaced stat goes to original position
        newIndex = this.dragOriginalIndex;
      } else {
        // Can't swap, find first available slot
        newIndex = this.findFirstFittingSlot(width, blocked);
      }

      if (newIndex !== -1) {
        moves.push({ fromIndex, toIndex: newIndex });
        // Mark new positions as blocked for subsequent displaced stats
        const { col, row } = this.indexToPosition(newIndex);
        for (let w = 0; w < width; w++) {
          blocked.add(this.positionToIndex(col + w, row));
        }
      }
    }

    // Clear dragging flag BEFORE applying changes
    // (so rebuildStats isn't blocked when save triggers listeners)
    this.dragging = null;

    // Apply all changes atomically
    userSettings.batchUpdateWithDisplacement(draggedKey, this.dragOriginalIndex, targetIndex, moves);
  }

  private startResize(stat: StatBlock, event: FederatedPointerEvent): void {
    this.resizing = stat;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = stat.widthMultiplier;
    this.resizeCurrentX = event.clientX;

    const onMove = (e: PointerEvent) => this.onResizeMove(e);
    const onUp = () => {
      this.onResizeEnd();
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }

  private onResizeMove(event: PointerEvent): void {
    if (!this.resizing) return;

    this.resizeCurrentX = event.clientX;
    const scale = window.innerWidth / SCENE_WIDTH;
    const dx = (event.clientX - this.resizeStartX) / scale;

    // Calculate new width in grid columns
    const newWidthCols = this.resizeStartWidth + dx / this.COL_WIDTH;
    const snappedWidth = Math.max(1, Math.min(3, Math.round(newWidthCols)));

    // Visual width spans full grid columns (minus gap)
    const blockWidth = snappedWidth * this.COL_WIDTH - 10;

    // Update background - highlight during resize
    this.resizing.bg.clear();
    this.resizing.bg.roundRect(0, 0, blockWidth, this.BLOCK_HEIGHT, 4);
    this.resizing.bg.fill({ color: this.colors.bgSurface, alpha: 1 });
    this.resizing.bg.stroke({ width: 1, color: this.colors.border, alpha: 0.8 });

    // Update pin icon position
    this.resizing.pinIcon.x = blockWidth - 22;

    // Update resize handle position (use x position, not rect position)
    this.resizing.resizeHandle.x = blockWidth - 6;
    this.resizing.resizeHandle.clear();
    this.resizing.resizeHandle.rect(0, 0, 12, this.BLOCK_HEIGHT);
    this.resizing.resizeHandle.fill({ color: this.colors.accentPrimary, alpha: 0.3 });

    // Update the tracked width multiplier for display
    this.resizing.widthMultiplier = snappedWidth;
  }

  private onResizeEnd(): void {
    if (!this.resizing) return;

    const scale = window.innerWidth / SCENE_WIDTH;
    const dx = (this.resizeCurrentX - this.resizeStartX) / scale;
    const newWidthCols = this.resizeStartWidth + dx / this.COL_WIDTH;
    const snappedWidth = Math.max(1, Math.min(3, Math.round(newWidthCols)));

    const resizingKey = this.resizing.key;
    const resizingIndex = this.resizing.gridIndex;
    const { col: resizingCol, row: resizingRow } = this.indexToPosition(resizingIndex);

    // Build occupancy map (excluding the resizing stat)
    const occupancy = this.buildOccupancyMap(resizingKey);

    // Find all stats that would be displaced by the new width (in the same row only)
    const statsToDisplace = new Map<string, number>(); // key -> anchor index

    for (let w = 1; w < snappedWidth; w++) {
      const checkIndex = this.positionToIndex(resizingCol + w, resizingRow);
      const occupyingKey = occupancy.get(checkIndex);
      if (occupyingKey && occupyingKey !== resizingKey && !statsToDisplace.has(occupyingKey)) {
        const anchorIndex = this.findStatAnchorIndex(occupyingKey);
        if (anchorIndex !== -1) {
          statsToDisplace.set(occupyingKey, anchorIndex);
        }
      }
    }

    // Build blocked set: positions occupied by stats NOT being displaced
    const blocked = new Set<number>();
    for (const [index, key] of occupancy) {
      if (!statsToDisplace.has(key)) {
        blocked.add(index);
      }
    }

    // Add positions the resizing stat will occupy
    for (let w = 0; w < snappedWidth; w++) {
      blocked.add(this.positionToIndex(resizingCol + w, resizingRow));
    }

    // Find new positions for displaced stats
    const moves: { fromIndex: number; toIndex: number }[] = [];

    for (const [key, fromIndex] of statsToDisplace) {
      const width = userSettings.getStatWidth(key);
      const newIndex = this.findFirstFittingSlot(width, blocked);

      if (newIndex !== -1) {
        moves.push({ fromIndex, toIndex: newIndex });
        // Mark new positions as blocked for subsequent displaced stats
        const { col, row } = this.indexToPosition(newIndex);
        for (let w = 0; w < width; w++) {
          blocked.add(this.positionToIndex(col + w, row));
        }
      }
    }

    // Clear resizing flag BEFORE applying changes
    // (so rebuildStats isn't blocked when save triggers listeners)
    this.resizing = null;

    // Apply all changes atomically
    userSettings.batchResizeWithDisplacement(resizingKey, snappedWidth, moves);
  }

  private drawUnpinIconVisible(g: Graphics): void {
    g.clear();
    // Hit area
    g.rect(0, 0, 18, 18);
    g.fill({ color: this.colors.bgPrimary, alpha: 0.01 });
    // Red X
    g.moveTo(4, 4);
    g.lineTo(14, 14);
    g.moveTo(14, 4);
    g.lineTo(4, 14);
    g.stroke({ width: 2, color: this.colors.error, alpha: 0.8 });
  }

  private drawUnpinIconHover(g: Graphics): void {
    g.clear();
    // Red box background
    g.roundRect(0, 0, 18, 18, 3);
    g.fill({ color: this.colors.error, alpha: 0.9 });
    // White X
    g.moveTo(5, 5);
    g.lineTo(13, 13);
    g.moveTo(13, 5);
    g.lineTo(5, 13);
    g.stroke({ width: 2, color: this.colors.textPrimary, alpha: 1 });
  }

  private createMoreStatsButton(): void {
    // Button moved to header - keeping container for compatibility but hidden
    this.moreStatsButton = new Container();
    this.moreStatsButton.visible = false;
  }

  private updateMoreStatsButtonPosition(): void {
    if (!this.moreStatsButton || !this.moreStatsButton.parent) return;
    this.moreStatsButton.x = 0;
    this.moreStatsButton.y = this.MAX_ROWS * this.ROW_HEIGHT + 8;
  }

  private drawButtonNormal(): void {
    this.moreStatsButtonBg.clear();
    this.moreStatsButtonBg.roundRect(0, 0, this.COL_WIDTH * 2 - 10, 24, 3);
    this.moreStatsButtonBg.stroke({ width: 1, color: this.colors.accentPrimary, alpha: 0.3 });
  }

  private drawButtonHover(): void {
    this.moreStatsButtonBg.clear();
    this.moreStatsButtonBg.roundRect(0, 0, this.COL_WIDTH * 2 - 10, 24, 3);
    this.moreStatsButtonBg.fill({ color: this.colors.accentPrimary, alpha: 0.1 });
    this.moreStatsButtonBg.stroke({ width: 1, color: this.colors.accentPrimary, alpha: 0.6 });
  }

  public setOnMoreStatsClick(callback: (mouseX: number, mouseY: number) => void): void {
    this.onMoreStatsClick = callback;
  }

  /**
   * Set callback to get circle metrics by address
   */
  public setCircleMetricsGetter(getter: (address: string) => CircleMetrics | null): void {
    this.getCircleMetrics = getter;
  }

  /**
   * Set callback to get project name by address
   * Rebuilds stats to update labels with project names
   */
  public setProjectNameGetter(getter: (address: string) => string | null): void {
    this.getProjectName = getter;
    // Rebuild stats to update labels now that we have project name lookup
    this.rebuildStats();
  }


  private formatNumber(num: number | undefined): string {
    if (num === undefined || num === null || isNaN(num)) return '0';
    // No decimals for large numbers - only use decimals for small numbers
    if (num >= 1_000_000_000) return Math.round(num / 1_000_000_000) + 'B';
    if (num >= 1_000_000) return Math.round(num / 1_000_000) + 'M';
    if (num >= 1_000) return Math.round(num / 1_000) + 'K';
    if (num >= 10) return Math.round(num).toString();
    if (num >= 1) return num.toFixed(1);
    if (num >= 0.01) return num.toFixed(2);
    return '0';
  }

  private formatNumberForWidth(num: number | undefined, widthMultiplier: number): string {
    if (num === undefined || num === null || isNaN(num)) return '0';

    if (widthMultiplier >= 3) {
      // Full precision with commas - no decimals
      if (num >= 1_000) return Math.round(num).toLocaleString();
      if (num >= 10) return Math.round(num).toString();
      if (num >= 1) return num.toFixed(1);
      return num.toFixed(2);
    } else if (widthMultiplier >= 2) {
      // Medium precision - no decimals for large numbers
      if (num >= 1_000_000_000) return Math.round(num / 1_000_000_000) + 'B';
      if (num >= 1_000_000) return Math.round(num / 1_000_000) + 'M';
      if (num >= 1_000) return Math.round(num).toLocaleString();
      if (num >= 10) return Math.round(num).toString();
      if (num >= 1) return num.toFixed(1);
      return num.toFixed(2);
    } else {
      // Compact (default)
      return this.formatNumber(num);
    }
  }

  private formatBlockNumber(num: number): string {
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(2) + 'M';
    if (num >= 1_000) return (num / 1_000).toFixed(1) + 'K';
    return num.toLocaleString();
  }

  private formatBlockNumberForWidth(num: number, widthMultiplier: number): string {
    if (widthMultiplier >= 2) {
      return num.toLocaleString();
    }
    return this.formatBlockNumber(num);
  }

  private getStatValueForKey(key: string): string {
    const widthMultiplier = userSettings.getStatWidth(key);

    // Check if it's an address-specific stat
    const parsed = parseAddressStatKey(key);
    if (parsed && this.getCircleMetrics) {
      const metrics = this.getCircleMetrics(parsed.address);
      if (metrics) {
        switch (parsed.statType) {
          case 'tps': return this.formatNumberForWidth(metrics.tps, widthMultiplier);
          case 'gps': return this.formatNumberForWidth(metrics.gps, widthMultiplier);
          case 'tx60s': return this.formatNumberForWidth(metrics.lastMinuteCount, widthMultiplier);
          case 'totalTx': return this.formatNumberForWidth(metrics.totalTxCount, widthMultiplier);
          case 'totalGas': return this.formatNumberForWidth(metrics.totalGasUsed, widthMultiplier);
        }
      }
      return '0';
    }

    // Global stats
    switch (key) {
      case 'tps': return this.formatNumberForWidth(this.state.tps?.value ?? 0, widthMultiplier);
      case 'gps': return this.formatNumberForWidth(this.state.gasPerSec?.value ?? 0, widthMultiplier);
      case 'mbps': return this.formatNumberForWidth(this.state.miniblocksPerSec?.value ?? 0, widthMultiplier);
      case 'bps': return this.formatNumberForWidth(this.state.blocksPerSec?.value ?? 0, widthMultiplier);
      case 'fbps': return this.formatNumberForWidth(this.state.frontendBps?.value ?? 0, widthMultiplier);
      case 'bebps': return (this.state.backendBpsValue?.value ?? 0).toFixed(4);
      case 'ubps': return (this.state.upstreamBps?.value ?? 0).toFixed(4);
      case 'fps': return Math.round(this.state.fps?.value ?? 60).toString();
      case 'block': return this.formatBlockNumberForWidth(this.state.currentBlock?.value ?? 0, widthMultiplier);
      default: return '0';
    }
  }

  private updateStat(key: string, value: string): void {
    const stat = this.stats.get(key);
    if (!stat) return;

    const prev = this.prevValues.get(key);
    if (value !== prev) {
      stat.value.text = value;
      this.prevValues.set(key, value);
      this.flashTimers.set(key, 1.0);
    }
  }

  /**
   * Draw sparkline for a stat block with smooth animation
   */
  private drawSparkline(stat: StatBlock, deltaMs: number): void {
    const history = this.state.getHistory(stat.key);

    if (history.length < 2) {
      stat.sparkline.clear();
      return;
    }

    // Calculate current min/max
    const currentMin = Math.min(...history);
    const currentMax = Math.max(...history);

    // Check if we need to redraw (new data point or scale changed significantly)
    const lengthChanged = history.length !== stat.sparklineLastLength;
    const scaleChanged = currentMin !== stat.sparklineTargetMin || currentMax !== stat.sparklineTargetMax;

    // Update targets when data changes
    if (scaleChanged) {
      stat.sparklineTargetMin = currentMin;
      stat.sparklineTargetMax = currentMax;
    }

    // Smoothly interpolate min/max for scale changes (lerp)
    const lerpFactor = Math.min(1, deltaMs * 0.008); // Smooth ~8ms response
    stat.sparklineLastMin += (stat.sparklineTargetMin - stat.sparklineLastMin) * lerpFactor;
    stat.sparklineLastMax += (stat.sparklineTargetMax - stat.sparklineLastMax) * lerpFactor;

    // Use interpolated min/max for drawing
    const min = stat.sparklineLastMin;
    const max = stat.sparklineLastMax;
    const range = max - min || 1;

    // Only redraw if something changed or animating
    const isAnimating = Math.abs(stat.sparklineLastMin - stat.sparklineTargetMin) > 0.01 ||
                        Math.abs(stat.sparklineLastMax - stat.sparklineTargetMax) > 0.01;

    if (!lengthChanged && !isAnimating) {
      return; // Skip redraw - nothing changed
    }

    stat.sparklineLastLength = history.length;

    const g = stat.sparkline;
    g.clear();

    const blockWidth = this.BLOCK_WIDTH * stat.widthMultiplier - 10;
    const padding = 8;
    const chartWidth = blockWidth - padding * 2;
    const chartHeight = 40;
    const chartY = this.BLOCK_HEIGHT - chartHeight - 8;

    // Draw from right to left (newest on right)
    // Use fixed number of points for consistent spacing
    const maxPoints = 60;
    const pointSpacing = chartWidth / (maxPoints - 1);

    // Start from the right side
    const startX = padding + chartWidth - (history.length - 1) * pointSpacing;

    g.moveTo(
      Math.max(padding, startX),
      chartY + chartHeight - ((history[0] - min) / range) * chartHeight
    );

    for (let i = 1; i < history.length; i++) {
      const x = startX + i * pointSpacing;
      if (x < padding) continue; // Skip points off the left edge
      const y = chartY + chartHeight - ((history[i] - min) / range) * chartHeight;
      g.lineTo(x, y);
    }

    g.stroke({
      color: this.colors.accentPrimary,
      alpha: 0.2,
      width: 1.5
    });
  }

  public update(deltaMs: number = 16.67): void {
    const showSparklines = userSettings.showSparklines;
    for (const [key, stat] of this.stats) {
      this.updateStat(key, this.getStatValueForKey(key));
      if (showSparklines) {
        this.drawSparkline(stat, deltaMs);
      } else if (stat.sparklineLastLength > 0) {
        // Clear sparkline if disabled and was previously drawn
        stat.sparkline.clear();
        stat.sparklineLastLength = 0;
      }
    }
    this.updateFlashEffects(deltaMs);
  }

  // OPTIMIZATION: Flash threshold - skip updates when flash is barely visible
  private readonly FLASH_THRESHOLD = 0.05;
  private readonly FLASH_DECAY = 0.1; // Decay rate per 16.67ms frame (0.9^1 at 60fps)

  private updateFlashEffects(deltaMs: number): void {
    for (const [key, stat] of this.stats) {
      const flash = this.flashTimers.get(key) ?? 0;
      if (flash > this.FLASH_THRESHOLD) {
        // OPTIMIZATION: Use scale animation instead of dropShadow (GPU-accelerated, no object allocations)
        // Subtle "pop" effect: scale from 1.0 to 1.15 based on flash intensity
        const scale = 1.0 + flash * 0.15;
        stat.value.scale.set(scale);
        // OPTIMIZATION: Delta-time aware decay - consistent speed at any FPS
        const decayFactor = Math.pow(1 - this.FLASH_DECAY, deltaMs / 16.67);
        this.flashTimers.set(key, flash * decayFactor);
      } else if (flash > 0 || this.flashTimers.has(key)) {
        // Flash dropped below threshold - reset scale and clean up
        stat.value.scale.set(1.0);
        this.flashTimers.delete(key);
      }
    }
  }

  /**
   * Get all stat block bounds for tutorial glow effect
   * Includes the resize handle area on the right edge
   */
  public getStatBlockBounds(): Array<{ x: number; y: number; width: number; height: number }> {
    const bounds: Array<{ x: number; y: number; width: number; height: number }> = [];
    const RESIZE_HANDLE_EXTRA = 6; // Half of resize handle extends past block
    for (const stat of this.stats.values()) {
      // Get world position (container position + stat container position)
      const worldX = this.container.x + stat.container.x;
      const worldY = this.container.y + stat.container.y;
      const width = this.BLOCK_WIDTH * stat.widthMultiplier + RESIZE_HANDLE_EXTRA;
      const height = this.BLOCK_HEIGHT;
      bounds.push({ x: worldX, y: worldY, width, height });
    }
    return bounds;
  }

  public destroy(): void {
    if (this.unsubscribeTheme) {
      this.unsubscribeTheme();
    }
    if (this.unsubscribeSettings) {
      this.unsubscribeSettings();
    }
    // Clear flash timers to prevent orphaned entries
    this.flashTimers.clear();
    this.prevValues.clear();
    this.container.destroy({ children: true });
  }
}
